const META_PIXEL_ID = '1780536449814138'

const PURCHASE_VALUE = 27
const PURCHASE_CURRENCY = 'USD'
const CONTENT_NAME = 'Makeup Instructor Ready'
const PURCHASE_FLAG_KEY = 'mir_purchase_tracked'

type MetaPixelFunction = {
  (...args: unknown[]): void
  callMethod?: { apply: (context: unknown, args: unknown) => void }
  push?: unknown
  queue?: unknown[]
  loaded?: boolean
  version?: string
}

declare global {
  interface Window {
    fbq?: MetaPixelFunction
    _fbq?: MetaPixelFunction
  }
}

function getPixel(): MetaPixelFunction | undefined {
  if (typeof window === 'undefined') return undefined

  // Reuse an fbq installed by another script instead of skipping initialization.
  if (window.fbq) return window.fbq

  const fbq: MetaPixelFunction = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, args)
    } else {
      fbq.queue?.push(args)
    }
  }

  fbq.push = fbq
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.queue = []

  window.fbq = fbq
  window._fbq = fbq

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/fr_FR/fbevents.js'
  document.head.appendChild(script)

  return fbq
}

/** Loads the pixel and fires PageView + ViewContent once per page load. */
export function initializeMetaPixel() {
  const fbq = getPixel()
  if (!fbq) return

  fbq('init', META_PIXEL_ID)
  fbq('track', 'PageView')
  fbq('trackSingle', META_PIXEL_ID, 'ViewContent', {
    content_name: CONTENT_NAME,
    content_type: 'product',
    value: PURCHASE_VALUE,
    currency: PURCHASE_CURRENCY,
  })

  trackPurchaseIfConfirmed()
}

/** Fired when a visitor clicks a checkout button. */
export function trackCheckoutStart() {
  window.fbq?.('trackSingle', META_PIXEL_ID, 'InitiateCheckout', {
    content_name: CONTENT_NAME,
    value: PURCHASE_VALUE,
    currency: PURCHASE_CURRENCY,
  })
}

/**
 * Purchase fires ONLY on a confirmed purchase (?purchase=success or a
 * /thank-you return URL), de-duplicated per order so reloads don't re-fire.
 */
export function trackPurchaseIfConfirmed() {
  if (typeof window === 'undefined') return

  const params = new URLSearchParams(window.location.search)
  const isConfirmed =
    params.get('purchase') === 'success' ||
    window.location.pathname.replace(/\/$/, '').endsWith('/thank-you')

  if (!isConfirmed) return

  const orderId = params.get('order_id') ?? params.get('order') ?? 'confirmed'
  const flag = `${PURCHASE_FLAG_KEY}:${orderId}`

  try {
    if (window.sessionStorage.getItem(flag)) return
    window.sessionStorage.setItem(flag, '1')
  } catch {
    // Storage unavailable — still fire once for this load.
  }

  window.fbq?.('trackSingle', META_PIXEL_ID, 'Purchase', {
    content_name: CONTENT_NAME,
    value: PURCHASE_VALUE,
    currency: PURCHASE_CURRENCY,
    ...(orderId !== 'confirmed' ? { order_id: orderId } : {}),
  })
}
